import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { DebugKey, createDebug } from '@ps/web-core-utils';

import type { GeoComplyPLC } from '@swf/core/types/Notification';

const geoComplyDebug = createDebug(DebugKey.GeoComply);
const envId = process.env.NODE_ENV === 'production' ? 'production' : 'staging';

const licenseToInstallID = {
  [LicenceId.USPA]: 'chiY9z83vt',
  [LicenceId.USMI]: 'LWWP2AmCC4',
  [LicenceId.USNJ]: 'DfjZEeypfN',
  [LicenceId.CAON]: '7rpHtFJyGc'
};

enum State {
  Idle,
  Connected,
  InProgress
}

interface OwnProps {
  isSiteUS: boolean;
  isSiteCAON: boolean;
  license: LicenceId;
  request(props: {
    service: string;
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'HEAD';
    body: { response: string; errorCode: number };
  }): void;
  enqueue(props: { path: string }): void;
}

type Props = GeoComplyPLC & OwnProps;

let state = State.Idle;

// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type BaseClient = typeof import('./sdk/geocomply-client.min').default.Client;

interface Client extends BaseClient {
  CLNT_ERROR_LOCAL_SERVICE_UNSUP_VER: number;
  CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE: number;

  connect(licenseId: string, envId: string): void;
  disconnect(): void;
  isConnected(): boolean;
  on(event: 'connect', cb: () => void): Client;
  on(event: 'error', cb: (errorCode: number, errorMessage: string) => void): Client;
  on(event: 'geolocation', cb: (data: string) => void): Client;
  requestGeolocation(): void;
  setGeolocationReason(reason: string): Client;
  setLicense(key: string): Client;
  setUserId(userId: string): Client;
}

export const geoComplyPLC = ({
  key,
  reason,
  id: userId,
  isSiteCAON,
  isSiteUS,
  license,
  request,
  enqueue
}: Props) => {
  import(/* webpackChunkName: "GeoComply" */ './sdk/geocomply-client.min').then((module) => {
    const Client = module.default.Client as Client;
    const requestGeolocationCheck = () => {
      geoComplyDebug('Geolocation request started');
      Client.setLicense(key).setGeolocationReason(reason).setUserId(userId);
      Client.requestGeolocation();
      state = State.InProgress;
    };

    if (!isSiteUS && !isSiteCAON) return;

    if (state === State.Idle) {
      state = State.InProgress;
      geoComplyDebug('Geolocation connection initiated');
      Client.connect(licenseToInstallID[license as keyof typeof licenseToInstallID], envId);

      Client.on('connect', () => {
        state = State.Connected;
        geoComplyDebug('GeoComply Client connected');
        requestGeolocationCheck();
      })
        .on('error', (errorCode: number, errorMessage: string) => {
          geoComplyDebug(
            `geoClient failed - errorCode: ${errorCode}, errorMessage: ${errorMessage}`
          );
          if (
            errorCode === Client.CLNT_ERROR_LOCAL_SERVICE_UNAVAILABLE ||
            errorCode === Client.CLNT_ERROR_LOCAL_SERVICE_UNSUP_VER
          ) {
            state = State.Idle;
            Client.disconnect();
            geoComplyDebug('PLC installation required');
            enqueue({ path: '/notifications/geo-comply' });
          } else {
            state = State.Connected;
          }
        })
        .on('geolocation', (data: string) => {
          geoComplyDebug(`GeoPacket created: ${data}`);
          request({
            service: '/api/v1-preview/account/geo-location/validate',
            method: 'POST',
            body: { response: data, errorCode: 0 }
          });
          state = State.Connected;
        });
    } else if (state === State.Connected && Client.isConnected()) {
      requestGeolocationCheck();
    }
  });
};
